import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle} from "lucide-react";
import { useMagicPrompt } from "@/hooks/useMagicPrompt";
import './ConversationSummaryApprovalDialogComponent.scss';
import { IRootReducerState } from "@/store/reducers";
import { useSelector } from "react-redux";
import { ConversationStep } from '@/store/actions/projectdata.action';

interface ConversationSummaryApprovalDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onApprove: (editedPrompt?: string) => void;
    prompt: string;
    isLoading?: boolean;
    isEditable?: boolean;
    showEditButton?: boolean;
    projectId?: string;
    isPmConversation?: boolean;
    currentConversationStep?: ConversationStep;
    // Dynamic content props
    title?: string;
    description?: string;
    buttonText?: string;
    loadingButtonText?: string;
    streamingButtonText?: string;
    cancelButtonText?: string;
    promptLabel?: string;
    placeholderText?: string;
    streamingPlaceholderText?: string;
}

const ConversationSummaryApprovalDialogComponent: React.FC<ConversationSummaryApprovalDialogProps> = ({
    isOpen,
    onClose,
    onApprove,
    prompt,
    isLoading = false,
    projectId,
    isPmConversation = false,
    currentConversationStep,
    // Dynamic content with defaults
    title = "Ready to Build Your App?",
    description = "Review the conversation summary below and approve to start building your application.",
    buttonText = "Start App Generation",
    loadingButtonText = "Starting App Generation...",
    streamingButtonText = "Generating Prompt...",
    cancelButtonText = "Cancel",
    promptLabel = "Conversation Summary:",
    placeholderText = "Edit your conversation summary...",
    streamingPlaceholderText = "Generating prompt..."
}) => {
    const [editedPrompt, setEditedPrompt] = useState(prompt);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const hasTriggeredPromptGeneration = React.useRef(false);
    const elevenLabsSessionId = useSelector((state: IRootReducerState) => state.projectData.elevenLabsSessionId);
    const currentPmSessionId = useSelector((state: IRootReducerState) => state.projectData.currentPmSessionId);
    const conversationSummaryPrompt = useSelector((state: IRootReducerState) => state.projectData.conversationSummaryPrompt);
    // Magic prompt hook
    const { enhancePrompt, isProcessing } = useMagicPrompt({
        onPromptUpdate: setEditedPrompt
    });
    // Update edited prompt when prompt prop or Redux prompt changes
    React.useEffect(() => {
        const currentPrompt = conversationSummaryPrompt || prompt;
        setEditedPrompt(currentPrompt);
    }, [prompt, conversationSummaryPrompt]);

    // Scroll to bottom of textarea
    const scrollToBottom = useCallback(() => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
    }, []);

    // Scroll to bottom when editedPrompt changes during streaming
    React.useEffect(() => {
        if (isProcessing) {
            scrollToBottom();
        }
    }, [editedPrompt, isProcessing, scrollToBottom]);

    // Handle PM conversation prompt generation
    const handlePmConversationPromptGeneration = useCallback(async () => {
        if (!projectId || !currentPmSessionId || isProcessing) return;

        const payload = currentConversationStep === 'requirement-gathering' ? {
            conversation_id: elevenLabsSessionId
        } : {
            pm_conversation_id: currentPmSessionId
        };

        await enhancePrompt(projectId, payload);
        
    }, [projectId, currentPmSessionId, enhancePrompt, isProcessing, currentConversationStep, elevenLabsSessionId]);

    // Start streaming when dialog opens for PM conversation (only if prompt is not set)
    useEffect(() => {
        const currentPrompt = conversationSummaryPrompt || prompt;
        if (isOpen && isPmConversation && !isProcessing && !hasTriggeredPromptGeneration.current && !currentPrompt.trim()) {
            hasTriggeredPromptGeneration.current = true;
            handlePmConversationPromptGeneration();
        }
        
        // Reset the trigger when dialog closes
        if (!isOpen) {
            hasTriggeredPromptGeneration.current = false;
        }
    }, [isOpen, isPmConversation, handlePmConversationPromptGeneration, isProcessing, prompt, conversationSummaryPrompt]);

    const handleApproveWithEdit = () => {
        // Use the edited prompt for approval
        onApprove(editedPrompt);
    };
    
    return (
        <Dialog open={isOpen} onOpenChange={()=>{}} >
            <DialogContent className="conversation-summary-dialog" >
                <DialogHeader >
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        {description}
                    </DialogDescription>
                </DialogHeader>
                
                <div className="prompt-preview-container">
                    <div className="prompt-label">
                        <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm mb-2">{promptLabel}</h4>
                        </div>
                    </div>
                    <div className="prompt-content">
                            <div className="edit-mode">
                                <div className="relative">
                                    <textarea
                                        ref={textareaRef}
                                        value={editedPrompt}
                                        onChange={(e) => setEditedPrompt(e.target.value)}
                                        className="prompt-textarea"
                                        rows={8}
                                        placeholder={isProcessing ? streamingPlaceholderText : placeholderText}
                                        disabled={isProcessing}
                                    />
                                    {isProcessing && (
                                        <div className="streaming-indicator">
                                            <div className="streaming-dots">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                            <span className="streaming-text">{streamingPlaceholderText}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading  || isProcessing}
                    >
                        <XCircle className="h-4 w-4" />
                        {cancelButtonText}
                    </Button>
                    <Button
                        onClick={handleApproveWithEdit}
                        disabled={isLoading  || isProcessing}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {loadingButtonText}
                            </>
                        ) : isProcessing ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {streamingButtonText}
                            </>
                        ) : (
                            <>
                                <CheckCircle className="h-4 w-4" />
                                {buttonText}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ConversationSummaryApprovalDialogComponent;
